import React from 'react';
import {
  Box,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { OnboardingFormProps } from 'src/components/Onboarding/OnboardingForms/onboardingFormTypes';
import {
  FILES_PAGE,
  FORMS_PAGE,
  KNOWLEDGE_BASE_PAGE,
  MESSAGES_PAGE,
  PAYMENTS_PAGE,
} from 'src/constants';
import { OnboardingButtonContainer } from 'src/components/Onboarding/OnboardingForms/OnboardingButtonContainer';
import SettingsToggleCard from 'src/components/Settings/SettingsToggleCard';
import * as Colors from 'src/theme/colors';
import OnboardingCard from 'src/components/Onboarding/OnboardingCard';
import { TrackEvent } from 'src/utils/AnalyticsUtils';

const schema = Yup.object().shape({});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: 'auto',
    width: 820,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  content: {
    padding: theme.spacing(2.25, 1, 0),
  },
  skipButton: {
    color: Colors.GraySmall,
  },
  submitButton: {
    [theme.breakpoints.only('xs')]: {
      position: 'sticky',
      bottom: 0,
    },
  },
}));

export const ModuleSettingsForm: React.FC<OnboardingFormProps> = ({
  children,
  initialValues,
  handleSubmitDone,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('xs'));

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values) => {
        const isSuccess = await handleSubmitDone(values);
        if (isSuccess) {
          TrackEvent('Onboarding - Module Setup Completed', {
            files: !values.filesPageDisabled,
            messages: !values.messagingPageDisabled,
            forms: !values.formsPageDisabled,
            billing: !values.paymentsModuleDisabled,
            knowledge_base: !values.resourceSectionDisabled,
          });
        }
      }}
    >
      {({ handleSubmit, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Box position="relative">
            <Box className={classes.root}>
              <OnboardingCard>
                <Box className={classes.content}>
                  <SettingsToggleCard
                    title={MESSAGES_PAGE.label}
                    Icon={MESSAGES_PAGE.icon}
                    fieldName="messagingPageDisabled"
                    description="Message existing clients directly from your portal and greet new clients with automatic welcome messages."
                    checked={!!values.messagingPageDisabled}
                    invertBooleanValue
                    hideIcon={isSmallWidth}
                  />
                  <SettingsToggleCard
                    title="File sharing"
                    Icon={FILES_PAGE.icon}
                    fieldName="filesPageDisabled"
                    description="Securely share files and request eSignatures. Replaces ShareFile, Dropbox, Google Drive, and DocuSign."
                    checked={!!values.filesPageDisabled}
                    invertBooleanValue
                    hideIcon={isSmallWidth}
                  />
                  <SettingsToggleCard
                    title={FORMS_PAGE.label}
                    Icon={FORMS_PAGE.icon}
                    fieldName="formsPageDisabled"
                    description="Send intake forms and surveys to clients to make client onboarding seamless. Replaces TypeForm and Google Forms."
                    checked={!!values.formsPageDisabled}
                    invertBooleanValue
                    hideIcon={isSmallWidth}
                  />
                  <SettingsToggleCard
                    title={PAYMENTS_PAGE.label}
                    Icon={PAYMENTS_PAGE.icon}
                    fieldName="paymentsModuleDisabled"
                    description="Create invoices & subscriptions and give clients an easy way to pay with credit card and ACH. Replaces Bill.com and QuickBooks."
                    checked={!!values.paymentsModuleDisabled}
                    invertBooleanValue
                    hideIcon={isSmallWidth}
                  />
                  <SettingsToggleCard
                    title={KNOWLEDGE_BASE_PAGE.label}
                    Icon={KNOWLEDGE_BASE_PAGE.icon}
                    fieldName="resourceSectionDisabled"
                    description="Create a knowledge base with text, images, and videos to help clients find answers to common questions. "
                    checked={!!values.resourceSectionDisabled}
                    invertBooleanValue
                    hideBorder
                    hideIcon={isSmallWidth}
                  />
                </Box>
              </OnboardingCard>
            </Box>
            <OnboardingButtonContainer>{children}</OnboardingButtonContainer>
          </Box>
        </form>
      )}
    </Formik>
  );
};
